import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { And, Rule, RuleInterface, StringListContains } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import {
  AllTristateBoolValues,
  TristateBool,
  getRuleFromTristateBool,
  getTristateBoolValueFromList,
} from '../../../common/controls/tristate-bool-helper';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';

interface SelectOption {
  id: string;
  label: string;
}

const SalesPersonActions: SelectOption[] = [
  {
    id: '*',
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ANY',
  },
  {
    id: 'inbound-call',
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ACTIONS.INBOUND_CALL',
  },
  {
    id: 'outbound-call',
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ACTIONS.OUTBOUND_CALL',
  },
  {
    id: 'email-sent',
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ACTIONS.EMAIL_SENT',
  },
  {
    id: 'email-received',
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ACTIONS.EMAIL_RECEIVED',
  },
  {
    id: 'meeting',
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ACTIONS.MEETING',
  },
  {
    id: 'other',
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ACTIONS.OTHER',
  },
];

const ConnectedStatus: SelectOption[] = [
  {
    id: TristateBool.ANY,
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.ANY',
  },
  {
    id: TristateBool.TRUE,
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.CONNECTED_STATUS.CONNECTED',
  },
  {
    id: TristateBool.FALSE,
    label: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_ACTIVITY_CREATED.CONNECTED_STATUS.NOT_CONNECTED',
  },
];

@Component({
  selector: 'automata-sales-activity-created-panel',
  styleUrls: ['./sales-activity-created-panel.component.scss'],
  templateUrl: './sales-activity-created-panel.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
  ],
})
export class SalesActivityCreatedPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  salespersonActions = SalesPersonActions;
  connectedStatusOptions = ConnectedStatus;
  selectedActions: string[] = [];
  selectedConnectionStatus: AllTristateBoolValues[] = [];

  ngOnInit(): void {
    super.ngOnInit();
    if (this.triggerChoices?.rules?.length > 0) {
      const and = And.fromRuleInterface(this.triggerChoices.rules[0]);
      this.selectedActions = and.value[0]?.value || [];
      this.selectedConnectionStatus = [getTristateBoolValueFromList(and.listValuesByVariablePath('{.connected}'))];
    }
    this.savingEnabled$.next(true);
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [];
  }

  isFormValid(): boolean {
    return this.selectedActions.length > 0 && this.selectedConnectionStatus.length > 0;
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    const andList: Rule[] = [new StringListContains('{.sales_person_action}', this.selectedActions)];
    return [new And(andList, '').toRuleInterface()];
  }

  fillRuleValues(_rules: RuleInterface[]): RuleInterface[] {
    const andList: Rule[] = [
      new StringListContains('{.sales_person_action}', this.selectedActions[0] === '*' ? ['*'] : this.selectedActions),
    ];
    andList.push(getRuleFromTristateBool('{.connected}', this.selectedConnectionStatus));
    return [new And(andList, '').toRuleInterface()];
  }
}
